import { type AuditText, auditMessage } from './text';

export interface AuditUserAgent {
    /** "Firefox 143 on Windows", "python-requests 2.32.3", or the start of the raw string. */
    text: AuditText;
    /** What the "user agent contains" filter sends for a click on the software. */
    filterText: string;
}

const RAW_LENGTH = 24;

// Scripted clients name themselves first; the version is everything up to a space.
const TOOL = /\b(python-requests|axios|curl|node-fetch)(?:\/([^\s;)]+))?/;

// Chromium browsers carry "Chrome/" too, so these are recognised as unknown rather
// than misread as Chrome.
const OTHER_CHROMIUM = /\b(OPR|SamsungBrowser|YaBrowser)\//;

const BROWSERS: ReadonlyArray<{ name: string; token: RegExp }> = [
    { name: 'Edge', token: /\b(?:Edg|EdgA|EdgiOS|Edge)\/(\d+)[\d.]*/ },
    { name: 'Firefox', token: /\b(?:Firefox|FxiOS)\/(\d+)[\d.]*/ },
    // Automated checks and scripted browsers; its token has no word boundary before Chrome.
    { name: 'Headless Chrome', token: /\bHeadlessChrome\/(\d+)[\d.]*/ },
    { name: 'Chrome', token: /\b(?:Chrome|CriOS)\/(\d+)[\d.]*/ },
    // Safari's own token is the WebKit build; its version is in Version/.
    { name: 'Safari', token: /\bVersion\/(\d+)[\d.]*(?=.*\bSafari\/)/ },
];

const SYSTEMS: ReadonlyArray<{ name: string; pattern: RegExp }> = [
    { name: 'Windows', pattern: /Windows NT/ },
    { name: 'iOS', pattern: /iPhone|iPad|iPod/ },
    { name: 'Android', pattern: /Android/ },
    { name: 'ChromeOS', pattern: /CrOS/ },
    { name: 'macOS', pattern: /Macintosh|Mac OS X/ },
    { name: 'Linux', pattern: /Linux/ },
];

function raw(userAgent: string): AuditUserAgent {
    const text = userAgent.length > RAW_LENGTH ? `${userAgent.slice(0, RAW_LENGTH)}…` : userAgent;
    return { text, filterText: userAgent };
}

function browser(userAgent: string): AuditUserAgent | null {
    if (OTHER_CHROMIUM.test(userAgent)) return null;
    for (const { name, token } of BROWSERS) {
        const match = token.exec(userAgent);
        if (match === null) continue;
        const system = SYSTEMS.find(({ pattern }) => pattern.test(userAgent))?.name;
        const values = { browser: name, version: match[1] };
        return {
            text:
                system === undefined
                    ? auditMessage('auditLog.source.browser', values)
                    : auditMessage('auditLog.source.browserOn', { ...values, os: system }),
            filterText: match[0],
        };
    }
    return null;
}

/**
 * The client software in a few words, with no parsing library: the families that reach
 * this API are browsers on the portal and a handful of HTTP clients on the v3 API.
 */
export function describeUserAgent(userAgent: string | null): AuditUserAgent | null {
    if (userAgent === null || userAgent.trim() === '') return null;
    const tool = TOOL.exec(userAgent);
    if (tool !== null) {
        return { text: tool[2] ? `${tool[1]} ${tool[2]}` : tool[1], filterText: tool[0] };
    }
    return browser(userAgent) ?? raw(userAgent);
}
